import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';

import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { displayActionCardHeader } from 'modules/automation/utils/displayActionCardHeader';
import { AssignToItemTemplate, AttachmentInput, FormLabel, TrackTime } from 'modules/automation/WorkflowBuilder/components';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { automationModules } from 'constants/automation';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { useFirmUserList } from 'hooks/useFirmUserList';
import { formatUserDropdown } from 'modules/automation/utils/formatUserDropdown';
import { generateFullName } from 'utils/generateFullNameUtils';

const CreateMessage = ({ index }) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const trigger = watch('trigger') || {};
  const is_for_lead = watch('is_lead_automation');
  const { userList, isFirmUserLoading } = useFirmUserList(true, false, true, true, '');

  const isForLeadAutomation = (trigger?.module === automationModules.LEADS || is_for_lead) && trigger?.code !== 'TRIG_LEAD_CONV_CASE';
  const from = watch(`actions[${index}].creator_firm_user_id`);

  const selectedGroupUsers = userList?.find((user) => user?.user_group_id === from?.value)?.users_list || [];

  return (
    trigger?.code !== 'TRIG_REVIEW_REQUEST' && (
      <>
        {displayActionCardHeader('Create New Secure Message')}

        <div className="row mb-2 g-3">
          <div className="col-12 col-md-6">
            <FormLabel label="From" isRequired />
            <Controller
              name={`actions[${index}].creator_firm_user_id`}
              control={control}
              rules={{ required: 'Message sender is required' }}
              render={({ field: { name, value, onChange, ref } }) => (
                <Dropdown
                  name={name}
                  value={value}
                  options={
                    formatUserDropdown({
                      users: userList,
                      hideInactive: true,
                      includeContacts: false,
                      includesUserGroups: true,
                      includesTeamMembers: true,
                    }) || []
                  }
                  onChange={(e) => {
                    onChange(e.value);
                    setValue(`actions[${index}].timeTracker.track_time_against_user_id`, null);
                  }}
                  className="w-100 input-shadow"
                  placeholder="Select"
                  optionLabel="label"
                  optionGroupLabel="groupLabel"
                  optionGroupChildren="items"
                  optionValue={(v) => ({ value: v.value, res_type: v.res_type })}
                  filter
                  itemTemplate={AssignToItemTemplate}
                  dropdownIcon={isFirmUserLoading ? 'pi pi-spin pi-spinner' : undefined}
                  inputRef={ref}
                />
              )}
            />
            <RequiredValidation errors={errors} field={`actions.${index}.creator_firm_user_id`} />
          </div>

          <div className="col-12 col-md-6">
            <FormLabel label="To" isRequired />
            <Controller
              name={`actions[${index}].selectedMessageTo`}
              control={control}
              rules={{ required: 'Message recipient is required' }}
              render={({ field: { name, value, onChange } }) => (
                <MultiSelect
                  name={name}
                  value={userList?.length && value?.length > 0 ? value : []}
                  options={
                    userList?.length
                      ? formatUserDropdown({
                          users: userList,
                          hideInactive: true,
                          includeSelectedInactiveUser: true,
                          includeSelectedInactiveUserGroup: true,
                          selectedItems: value || [],
                          includeContacts: false,
                          includesUserGroups: true,
                          includesTeamMembers: true,
                          includePrimarySecondary: true,
                          isLead: isForLeadAutomation,
                        })
                      : []
                  }
                  onChange={(e) => onChange(e.value)}
                  className="w-100 input-shadow"
                  placeholder="Select"
                  display={value?.length > 0 ? 'chip' : ''}
                  optionLabel="label"
                  optionGroupLabel="groupLabel"
                  optionGroupChildren="items"
                  optionValue={(v) => ({ value: v.value, res_type: v.res_type })}
                  isFilter
                  itemTemplate={AssignToItemTemplate}
                  optionDisabled={(option) => option?.status === 'ARCHIVED'}
                  dropdownIcon={isFirmUserLoading ? 'pi pi-spin pi-spinner' : undefined}
                  filter
                />
              )}
            />
            <RequiredValidation errors={errors} field={`actions.${index}.selectedMessageTo`} />
            {isForLeadAutomation && (
              <div className="d-flex mt-2 files-note">
                <i className="pi pi-info-circle me-1"></i>
                If 'On Behalf Of' contact is not available, 'Client' will be used automatically, even if not selected.
              </div>
            )}
          </div>

          {/* SUBJECT */}
          <div className="col-12 col-md-6">
            <FormLabel label="Subject" isRequired />
            <Controller
              name={`actions[${index}].subject`}
              control={control}
              rules={{ required: 'Subject is required' }}
              defaultValue=""
              render={({ field: { name, value, onChange } }) => (
                <InputText
                  name={name}
                  value={value}
                  onChange={(e) => onChange(e.target.value || '')}
                  placeholder="Subject"
                  className="w-100 input-shadow"
                  autoComplete="off"
                />
              )}
            />
            <RequiredValidation errors={errors} field={`actions.${index}.subject`} />
          </div>

          {/* MESSAGE (full width) */}
          <div className="col-12">
            <FormLabel label="Message" isRequired />
            <div className="create-msg-popup w-100">
              <Controller
                name={`actions[${index}].message`}
                defaultValue=""
                control={control}
                rules={{ required: 'Message is required' }}
                render={({ field: { name, value, onChange } }) => (
                  <TextSnippetWrapper
                    type="editor"
                    value={sanitizeHtmlColorContent(value)}
                    name={name}
                    onChange={(e) => onChange(e)}
                    className="input-shadow"
                    autoComplete="off"
                  />
                )}
              />
              <RequiredValidation errors={errors} field={`actions.${index}.message`} />
            </div>
          </div>
          {/* ATTACHMENTS */}
          <div className="col-12">
            <FormLabel label="Attachments" />
            <AttachmentInput index={index} />
          </div>

          {/* TRACK TIME */}
          <div className="col-12">
            {from?.res_type === 'user-group' && (
              <div className="col-12 col-md-6 mb-2">
                <FormLabel label="Track activity/time against which user?" isRequired />
                <Controller
                  name={`actions[${index}].group_member_user_id`}
                  defaultValue=""
                  control={control}
                  rules={{ required: 'User is required' }}
                  render={({ field: { name, value, onChange } }) => (
                    <Dropdown
                      name={name}
                      value={value}
                      options={selectedGroupUsers}
                      optionLabel={(user) => generateFullName(user)}
                      optionValue="firm_user_id"
                      onChange={(e) => onChange(e.value)}
                      className="input-shadow w-100"
                      dropdownIcon={isFirmUserLoading ? 'pi pi-spin pi-spinner' : undefined}
                    />
                  )}
                />
                <RequiredValidation errors={errors} field={`actions.${index}.timeTracker.track_time_against_user_id`} />
              </div>
            )}
            <TrackTime index={index} />
          </div>
        </div>
      </>
    )
  );
};

export default CreateMessage;
